'use client';

import { ElementType, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.js';
import { Link } from '@sola/ark-core/atoms/link/link.js';
import {
  getArticlePath,
  getCategoryTaxonomy,
} from '@sola/contentful-model/lib/article.js';
import { ArticleTypesEnum } from '@sola/contentful-model/lib/taxonomy-values.js';
import { QueryResponsePublished } from '@sola/skippy-search-sdk/lib/schema.js';
import { slugify, trailingSlash } from '@sola/utils/string.js';

export const ArticleTabsEnum = z.enum([
  'all',
  'topic',
  'theme',
  'country',
  'search',
]);
export type ArticleTabsEnum = z.infer<typeof ArticleTabsEnum>;

export const ArticleTermListProps = z.object({
  terms: z.array(z.object({ path: z.string(), name: z.string() })),
  group: ArticleTabsEnum, // todo: don't do this in zod 4
});

export type ArticleTermListProps = z.infer<typeof ArticleTermListProps>;

type ArticleTermListTypes = {
  seoLevel: number;
  tabGroup: ArticleTabsEnum;
  articleType: ArticleTypesEnum;
  facets: QueryResponsePublished['facets'];
};

export const ArticleTermList: React.FC<ArticleTermListTypes> = ({
  seoLevel = 3,
  tabGroup,
  articleType,
  facets,
}) => {
  const pathname = getArticlePath('tag', articleType);
  const taxonomy = getCategoryTaxonomy(tabGroup);
  const isCountry = tabGroup === 'country';

  const { t } = useTranslation();

  const terms = useMemo(() => {
    const parsed = facets[0]?.values.map((term) => {
      let termName = t(
        `skippy.taxonomy.${taxonomy}.${term.key}.name`,
        t(`skippy.taxonomy.all`),
      );
      if (isCountry) {
        const country = term.key.toUpperCase();
        termName = t(`geo.countries.${country}.nameS`);
      }

      return {
        path: `${trailingSlash(pathname)}${slugify(termName)}/`,
        name: termName,
      };
    });

    return parsed?.sort((a, b) => a.name.localeCompare(b.name));
  }, [facets]);

  const getTabContent = (): ArticleTermListProps | undefined => {
    switch (tabGroup) {
      case ArticleTabsEnum.enum.country:
        return {
          terms: terms || [],
          group: ArticleTabsEnum.enum.country,
        };
      case ArticleTabsEnum.enum.theme:
        return {
          terms: terms || [],
          group: ArticleTabsEnum.enum.theme,
        };
      case ArticleTabsEnum.enum.topic:
        return {
          terms: terms || [],
          group: ArticleTabsEnum.enum.topic,
        };
      default:
        return undefined;
    }
  };

  const tabContent = getTabContent();

  const Comp = `h${seoLevel}` as ElementType;

  return (
    <ul className="ArticleTermList m-none p-none gap-comfy-lg grid grid-cols-1 md:grid-cols-3 md:grid-flow-col">
      {tabContent?.terms.map((term) => {
        return (
          <li className="ArticleTermList__item" key={term.path}>
            <LinkStyle noUnderline size="lg" purpose="neutral" noPadding>
              <Link href={term.path} title={term.name}>
                <Comp className="label-potent body-md">{term.name}</Comp>
              </Link>
            </LinkStyle>
          </li>
        );
      })}
    </ul>
  );
};
